/**
 * <venner-engraving>
 *
 * Campo de iniciales para grabado. Reemplaza a la app de personalización.
 *
 * Responsabilidades:
 *   - Mostrar u ocultar el campo según la variante seleccionada (evento `variant:change` de Prestige).
 *   - Forzar mayúsculas y filtrar caracteres no grabables mientras el cliente escribe.
 *   - Bloquear el checkout (incluido el botón de pago dinámico) si faltan las iniciales.
 *   - Permitir renunciar al grabado en una pieza concreta (bundles).
 *
 * Cuando el campo está oculto o el cliente marca "sin grabado", el input se deshabilita: así no se envía
 * la propiedad al carrito y tampoco bloquea la validación nativa del formulario.
 */
if (!window.customElements.get('venner-engraving')) {
  const ALLOWED_CHARACTERS = /[^A-Z0-9Ñ]/g;

  class VennerEngraving extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();

      const { signal } = this.abortController;

      this.input = this.querySelector('[data-engraving-input]');
      this.errorMessage = this.querySelector('[data-engraving-error]') || this.querySelector('.venner-engraving__error');
      this.optOutToggle = this.querySelector('[data-engraving-opt-out-toggle]');
      this.optOutValue = this.querySelector('[data-engraving-opt-out-value]');
      this.helpButton = this.querySelector('[data-engraving-help]');
      this.helpWrap = this.querySelector('.venner-engraving__help-wrap');
      this.tooltip = this.querySelector('.venner-engraving__tooltip');

      if (!this.input) {
        return;
      }

      this.input.addEventListener('input', this.onInput.bind(this), { signal });
      this.input.addEventListener('invalid', this.onInvalid.bind(this), { signal });
      this.input.addEventListener('blur', this.refreshValidity.bind(this), { signal });

      if (this.optOutToggle) {
        this.optOutToggle.addEventListener('change', this.onOptOutChange.bind(this), { signal });
      }

      if (this.helpButton) {
        this.helpButton.addEventListener('click', this.toggleTooltip.bind(this), { signal });
        document.addEventListener('click', this.onDocumentClick.bind(this), { signal });
        document.addEventListener('keydown', this.onDocumentKeydown.bind(this), { signal });

        // El hover y el foco abren el globo desde el CSS, así que no hay un único momento en el
        // que engancharse: se mide justo antes de cada forma de abrirlo, y otra vez cuando
        // cambia el tamaño de la ventana.
        const place = this.placeTooltip.bind(this);

        this.helpWrap?.addEventListener('pointerenter', place, { signal });
        this.helpButton.addEventListener('focus', place, { signal });
        window.addEventListener('resize', place, { passive: true, signal });
      }

      // El formulario se declara más abajo en el DOM (vive dentro de buy_buttons), así que al ejecutarse
      // connectedCallback todavía no existe. `variant:change` burbujea y trae el id del formulario en el
      // detalle, de modo que escuchar en document evita depender del orden de los bloques.
      document.addEventListener('variant:change', this.onVariantChange.bind(this), { signal });

      // Red de seguridad. El estado del campo decide si unas iniciales viajan o no al pedido, así que no
      // puede depender de que un evento haya llegado bien: justo antes de enviar se vuelve a leer la
      // variante que el servidor tiene pintada y se recalcula. Va en fase de captura para adelantarse al
      // <product-form> de Prestige, que en cuanto recibe el submit arma el FormData y lo manda al carrito.
      document.addEventListener('submit', this.onFormSubmit.bind(this), { capture: true, signal });

      // El botón de pago dinámico (PayPal, Shop Pay…) no dispara la validación nativa del formulario, así
      // que lo interceptamos en fase de captura antes de que Shopify lo procese.
      document.addEventListener('click', this.onPaymentButtonClick.bind(this), { capture: true, signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    /**
     * Se pregunta al propio input por su formulario: `input.form` es la asociación que hace el navegador a
     * partir del atributo `form=`, y es más fiable que buscarlo a mano en `document.forms`.
     */
    get form() {
      return this.input?.form || document.forms[this.getAttribute('form')] || null;
    }

    get maxLength() {
      return parseInt(this.getAttribute('max-length'), 10) || 0;
    }

    get visibility() {
      return this.getAttribute('visibility') || 'variant';
    }

    get triggerValues() {
      return (this.getAttribute('trigger-values') || '')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
    }

    get isRequired() {
      return this.hasAttribute('data-required');
    }

    get isOptedOut() {
      return Boolean(this.optOutToggle?.checked);
    }

    /**
     * Normaliza lo que el cliente escribe: mayúsculas, sin acentos y solo letras y números.
     * Conserva la posición del cursor para que se pueda corregir a media palabra.
     */
    onInput() {
      const cursorPosition = this.input.selectionStart;
      const previousValue = this.input.value;
      const nextValue = previousValue
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u0302\u0304-\u0308\u030A-\u036F]/g, '') // conserva la tilde de la Ñ (U+0303)
        .normalize('NFC')
        .replace(ALLOWED_CHARACTERS, '');

      if (nextValue !== previousValue) {
        this.input.value = nextValue;
        const offset = previousValue.length - nextValue.length;
        this.input.setSelectionRange(Math.max(0, cursorPosition - offset), Math.max(0, cursorPosition - offset));
      }

      this.refreshValidity();
    }

    /**
     * Falta rellenar: el campo está activo, el cliente no marcó "sin grabado" y no escribió nada.
     * Se comprueba a mano porque la validación nativa no siempre corre: el pago rápido la salta, y
     * cualquier envío hecho desde JavaScript también.
     */
    get isMissing() {
      return (
        this.isRequired &&
        !this.isOptedOut &&
        !this.hidden &&
        !this.input.disabled &&
        this.input.value.trim() === ''
      );
    }

    showError() {
      this.errorMessage?.removeAttribute('hidden');
      this.input.setAttribute('aria-invalid', 'true');

      const focused = document.activeElement;

      if (focused !== this.input && focused?.matches?.('input:invalid, select:invalid, textarea:invalid')) {
        return;
      }

      this.input.focus();
    }

    onInvalid(event) {
      // Cancelamos el globo nativo del navegador ("Fill out this field"): sale en inglés, tapa el campo
      // y repite lo que ya dice el aviso rojo de abajo. El campo sigue siendo inválido, así que el
      // formulario se sigue sin poder enviar.
      event.preventDefault();

      // Al cancelar el globo, el navegador tampoco lleva el foco al campo; showError() lo hace, y sin
      // robárselo a otro campo inválido que ya lo tenga (en los sets hay varias piezas con iniciales).
      this.showError();
    }

    refreshValidity() {
      if (this.input.validity.valid) {
        this.errorMessage?.setAttribute('hidden', '');
        this.input.removeAttribute('aria-invalid');
      }
    }

    onOptOutChange() {
      const optedOut = this.isOptedOut;

      this.input.disabled = optedOut;
      this.input.required = !optedOut && this.isRequired;

      if (optedOut) {
        this.input.value = '';
        this.refreshValidity();
      }

      if (this.optOutValue) {
        this.optOutValue.disabled = !optedOut;
      }

      this.classList.toggle('venner-engraving--opted-out', optedOut);
    }

    /**
     * En modo automático no hace falta configurar nada: las variantes se llaman "Con Grabado de Iniciales"
     * y "Sin grabado de iniciales", así que basta con descartar primero la negativa.
     */
    matchesVariant(variantTitle) {
      if (this.visibility === 'auto') {
        return !variantTitle.includes('sin grabado') && variantTitle.includes('grabado');
      }

      return this.triggerValues.some((triggerValue) => variantTitle.includes(triggerValue));
    }

    onVariantChange() {
      // No se filtra por formulario: `sync()` lee la variante del selector de ESTE campo, así que un
      // evento de otro producto de la página no puede afectarle. Filtrar aquí solo añadía una forma de
      // quedarse congelado si la comparación fallaba.
      this.sync();
    }

    onFormSubmit(event) {
      // Resincronizar no se filtra por formulario: de más no rompe nada, y de menos manda al pedido unas
      // iniciales que el cliente no pidió.
      this.sync();

      // Bloquear sí se filtra: no podemos frenar el formulario de otro producto de la página.
      if (event.target !== this.form || !this.isMissing) {
        return;
      }

      // `stopImmediatePropagation` además de `stopPropagation`: <product-form> de Prestige escucha el
      // submit en el mismo nodo, y sin esto armaría el FormData y lo mandaría al carrito igual.
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError();
    }

    /**
     * La verdad sobre qué variante está elegida no la trae el evento, sino el JSON que Prestige repinta
     * dentro del selector de variantes en cada cambio. Leerlo de ahí hace que el campo quede bien aunque
     * no haya llegado ningún evento.
     */
    selectedVariantTitle() {
      const formId = this.getAttribute('form');
      const script = document.querySelector(`variant-picker[form-id="${formId}"] script[data-variant]`);

      if (!script) {
        return null;
      }

      try {
        return (JSON.parse(script.textContent)?.title || '').toLowerCase();
      } catch (error) {
        return null;
      }
    }

    sync() {
      if (this.visibility === 'always') {
        return;
      }

      if (this.visibility === 'variant' && this.triggerValues.length === 0) {
        return;
      }

      const variantTitle = this.selectedVariantTitle();

      if (variantTitle === null) {
        return;
      }

      const isActive = this.matchesVariant(variantTitle);

      this.hidden = !isActive;
      this.input.disabled = !isActive || this.isOptedOut;
      this.input.required = isActive && !this.isOptedOut && this.isRequired;

      if (this.optOutValue) {
        this.optOutValue.disabled = !isActive || !this.isOptedOut;
      }

      if (!isActive) {
        // Se vacía además de deshabilitarse: si por lo que sea el campo llegara a enviarse, que no lleve
        // unas iniciales que el cliente ya descartó.
        this.input.value = '';
        this.refreshValidity();
      }
    }

    onPaymentButtonClick(event) {
      const paymentButton = event.target.closest?.('.shopify-payment-button');

      if (!paymentButton) {
        return;
      }

      const form = this.form;

      if (form?.contains(paymentButton)) {
        // El pago rápido salta el submit, así que aquí también hay que resincronizar antes de validar.
        this.sync();
      }

      if (!form?.contains(paymentButton)) {
        return;
      }

      if (this.isMissing) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError();
        return;
      }

      if (!form.checkValidity()) {
        event.preventDefault();
        event.stopPropagation();
        form.reportValidity();
      }
    }

    /**
     * El hover y el foco los resuelve el CSS. Aquí solo se cubre el toque en móvil, donde no hay hover.
     */
    toggleTooltip() {
      this.placeTooltip();
      this.helpWrap?.toggleAttribute('data-open');
    }

    /**
     * El globo va centrado sobre el icono «?», que queda al principio del renglón: en un teléfono
     * se sale por el borde de la pantalla y las primeras palabras se cortan. Se le empuja de vuelta
     * hacia adentro; la flecha se mueve al revés desde el CSS para seguir apuntando al icono.
     * Se puede medir aunque el globo esté en `visibility: hidden`: sigue ocupando su lugar.
     */
    placeTooltip() {
      if (!this.tooltip) return;

      this.tooltip.style.setProperty('--engraving-tooltip-shift', '0px');

      const gutter = 12;
      const box = this.tooltip.getBoundingClientRect();
      let shift = 0;

      if (box.left < gutter) {
        shift = gutter - box.left;
      } else if (box.right > window.innerWidth - gutter) {
        shift = window.innerWidth - gutter - box.right;
      }

      this.tooltip.style.setProperty('--engraving-tooltip-shift', `${Math.round(shift)}px`);
    }

    closeTooltip() {
      this.helpWrap?.removeAttribute('data-open');
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.closeTooltip();
      }
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.closeTooltip();
      }
    }
  }

  window.customElements.define('venner-engraving', VennerEngraving);
}
